import { Box, Divider, RadioGroup, SelectChangeEvent, styled, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../../components/Button/Button';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import Dropdown from '../../../../components/Dropdown/Dropdown';
import { DropdownItem } from '../../../../components/Dropdown/DropdownModels';
import Radio from '../../../../components/Radio/Radio';
import SectionTitle from '../../../../components/Text/TextStyles';
import WarningIconTransparent from '../../../../components/Icons/WarningIconTransparent';
import { NotificationSetting, NotificationType } from '../../../../models/CompanyOnboardingModels';
import { Tariff } from '../../../../models/MarketsModels';
import { updateImportRequest } from '../../../../stores/ImportStore';
import { RootState } from '../../../../stores/Store';
import { UploadType } from '../../../../models/ImportModels';

const RowsContainer = styled(Box)(() => ({
  paddingBottom: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
}));

const Row = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
}));

const SectionValue = styled(Typography)(() => ({
  lineHeight: 'normal',
  marginLeft: 'auto',
  fontWeight: '700',
}));

const SectionLabel = styled(Typography)(() => ({
  lineHeight: 'normal',
}));

const WarningContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.container.background,
  marginTop: '1rem',
  marginBottom: '1.5rem',
  borderRadius: '0.375rem',
  border: `1px solid ${theme.palette.warning.main}`,
}));

interface BulkImportSummaryStepProps {
  onClose: () => void;
  selectedBulkImports: number;
  nextStep: () => void;
  isLoading: boolean;
  availableTariffs: Tariff[];
}

function BulkImportSummaryStep({
  onClose,
  selectedBulkImports,
  nextStep,
  isLoading,
  availableTariffs,
}: BulkImportSummaryStepProps) {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const {
    request,
    qrCodeNotificationSettings,
    hasDistributionList,
    importResponse,
    bulkImportValidRowsResponse,
    transferPullEnabled,
  } = useSelector((state: RootState) => state.importReducer);
  const [tariffOptions, setTariffOptions] = useState<DropdownItem[]>([]);
  const [selectedTariff, setSelectedTariff] = useState<string>('');
  const [showTariffError, setShowTariffError] = useState(false);

  useEffect(() => {
    if (availableTariffs.length) {
      setShowTariffError(false);
      const options: DropdownItem[] = availableTariffs.map((tariff) => ({
        value: tariff.code,
        label: tariff.description,
        key: tariff.code,
        data: tariff,
      }));
      setTariffOptions(options);
      if (request.tariffCode) {
        setSelectedTariff(request.tariffCode);
      }
    } else {
      setShowTariffError(true);
    }
  }, [availableTariffs]);

  /**
   * Handles change of the transfer radio button
   * @param e
   */
  const handleTransferRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateImportRequest({ confirmTransfer: e.target.value === 'true' }));
  };

  /**
   * Handle tariff dropdown change
   * @param e
   */
  const handleTariffChange = (e: SelectChangeEvent<unknown>) => {
    setSelectedTariff(e.target.value as string);
    dispatch(updateImportRequest({ tariffCode: e.target.value as string }));
  };

  /**
   * Handles change of the radio button
   * @param e
   */
  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value as NotificationType;
    dispatch(updateImportRequest({ qrCodeNotification: val }));
  };

  /**
   * Return translation based on upload type
   */
  const getNumOfSelectionsTranslation = () => {
    switch (importResponse?.uploadType) {
      case UploadType.SIM_SWAPS:
        return t('pages.bulkImport.summaryDrawer.numberOfSimSwaps');
      case UploadType.NEW_CONNECTIONS:
        return t('pages.bulkImport.summaryDrawer.numberOfNewConnections');
      default:
        return '';
    }
  };

  /**
   * Check if bulk is eligible for transfer
   */
  const isTransferEligible = (): boolean => {
    if (!bulkImportValidRowsResponse?.transfer) {
      return false;
    }

    return request.notificationType === 'PULL' ? transferPullEnabled : true;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Summary */}
      <SectionTitle>{t('pages.bulkImport.summaryDrawer.summary')}</SectionTitle>
      <RowsContainer>
        <Row>
          <SectionLabel>{getNumOfSelectionsTranslation()}</SectionLabel>
          <SectionValue>{selectedBulkImports}</SectionValue>
        </Row>
        <Row>
          <SectionLabel>{t('pages.bulkImport.summaryDrawer.excludedLines')}</SectionLabel>
          <SectionValue>{bulkImportValidRowsResponse?.excludedLines}</SectionValue>
        </Row>
        <Row>
          <SectionLabel>{t('pages.bulkImport.summaryDrawer.activationMethod')}</SectionLabel>
          <SectionValue>
            {t(`pages.bulkImport.summaryDrawer.${request.notificationType.toLocaleLowerCase()}`)}
          </SectionValue>
        </Row>
      </RowsContainer>
      {importResponse?.uploadType === UploadType.NEW_CONNECTIONS && availableTariffs.length > 1 ? (
        <Box>
          <Dropdown
            id="tariffCode"
            name="tariffCode"
            label={t('pages.bulkImport.summaryDrawer.tariffCode')}
            value={selectedTariff}
            onChange={(e) => handleTariffChange(e)}
            formStyle={{ padding: '1.5rem 0' }}
            items={tariffOptions}
            disabled={!availableTariffs || availableTariffs.length === 1}
            placeholder={
              t('common.placeholders.dropdownPlaceholder') + t('pages.bulkImport.summaryDrawer.tariffPlaceholder')
            }
          />
        </Box>
      ) : null}
      {importResponse?.uploadType === UploadType.NEW_CONNECTIONS && showTariffError ? (
        <Typography sx={{ color: (theme) => theme.palette.state.error, fontSize: '1.125rem', paddingBottom: '1.5rem' }}>
          {t('pages.bulkImport.summaryDrawer.noTariffs')}
        </Typography>
      ) : null}

      {importResponse?.uploadType === UploadType.SIM_SWAPS ? (
        <>
          {isTransferEligible() ? (
            <Box sx={{ marginTop: '1rem' }}>
              <RadioGroup
                aria-labelledby="source-device-access"
                name="source-device-access"
                value={request.confirmTransfer.toString()}
                onChange={handleTransferRadioChange}
              >
                <Radio value="true" label={t('pages.bulkImport.summaryDrawer.transferUserHasAccess')} />
                <Radio value="false" label={t('pages.bulkImport.summaryDrawer.transferNoAccess')} />
              </RadioGroup>
            </Box>
          ) : null}
          {request.confirmTransfer ? (
            <WarningContainer>
              <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '1rem' }}>
                <WarningIconTransparent />
                <Typography sx={{ fontWeight: '700' }}>
                  {t('pages.userDetails.upgradeDeviceDrawer.transferWarningTitle')}
                </Typography>
              </Box>
              <Typography sx={{ padding: '1rem', paddingTop: 0 }}>
                <Trans
                  i18nKey="pages.userDetails.upgradeDeviceDrawer.transferWarningMessage1"
                  components={{ Bold: <strong /> }}
                />
              </Typography>
              <Typography sx={{ padding: '1rem', paddingTop: 0 }}>
                {t('pages.userDetails.upgradeDeviceDrawer.transferWarningMessage2')}
              </Typography>
            </WarningContainer>
          ) : (
            <Box
              width="100%"
              sx={{
                backgroundColor: (theme) => theme.palette.monochrome.monoChrome2,
                marginTop: '1rem',
                marginBottom: '1.5rem',
              }}
            >
              <Typography sx={{ padding: '1rem' }}>
                {t('pages.bulkImport.summaryDrawer.upgradeWarningMessage')}
              </Typography>
            </Box>
          )}
        </>
      ) : null}

      {/* Notification */}
      <SectionTitle sx={{ paddingTop: '1rem' }}>
        {t('pages.bulkImport.summaryDrawer.notificationSettings')}
      </SectionTitle>
      <Box sx={{ paddingBottom: '1rem' }}>
        <Typography sx={{ paddingBottom: '1.5rem' }}>
          {request.notificationType === 'PUSH'
            ? t('pages.bulkImport.summaryDrawer.qrCodeQuestion_push')
            : t('pages.bulkImport.summaryDrawer.qrCodeQuestion')}
        </Typography>
        <RadioGroup
          aria-labelledby="qr-code-destination"
          name="qr-code-destination"
          value={request.qrCodeNotification}
          onChange={handleRadioChange}
        >
          {qrCodeNotificationSettings.map((ns: NotificationSetting) => (
            <Radio
              value={ns.type}
              key={ns.type}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                  {t(`pages.bulkImport.summaryDrawer.${ns.type}`)}
                  <Typography sx={{ paddingLeft: '1rem', color: (theme) => theme.palette.text.disabled }}>
                    {ns.label}
                  </Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
        {qrCodeNotificationSettings.length === 0 ? (
          <Typography sx={{ color: (theme) => theme.palette.primary.main }}>
            {t('pages.bulkImport.summaryDrawer.noNotificationSettings')}
          </Typography>
        ) : null}
      </Box>

      <Divider
        sx={{
          borderColor: (theme) => theme.palette.monochrome.monoChrome3,
          marginBottom: '1.5rem',
        }}
      />

      <Box sx={{ marginBottom: '1rem' }}>
        <Checkbox
          checked={request.notifyAdmin}
          onChange={(e) => {
            dispatch(updateImportRequest({ notifyAdmin: e.currentTarget.checked }));
          }}
          label={t('pages.bulkImport.summaryDrawer.sendMeNotifications')}
        />
      </Box>

      {hasDistributionList ? (
        <Box sx={{ marginBottom: '2.5rem' }}>
          <Checkbox
            checked={request.notifyDistributionList}
            onChange={(e) => {
              dispatch(updateImportRequest({ notifyDistributionList: e.currentTarget.checked }));
            }}
            label={t('pages.bulkImport.summaryDrawer.sendDLNotifications')}
          />
        </Box>
      ) : null}

      <Box sx={{ marginTop: 'auto' }}>
        <ButtonGroup>
          <Button label={t('common.cancel')} color="alternative2Button" onClick={onClose} disabled={isLoading} />
          <Button
            label={t('common.send')}
            color="primaryButton"
            onClick={nextStep}
            disabled={
              isLoading ||
              (importResponse?.uploadType === UploadType.NEW_CONNECTIONS && (showTariffError || !selectedTariff)) ||
              sessionStorage.getItem('impersonateId') !== null
            }
          />
        </ButtonGroup>
      </Box>
    </Box>
  );
}

export default BulkImportSummaryStep;
